import { Component, EventEmitter, forwardRef, inject, input, Output, signal, viewChild } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { AbstractFormControl } from '@shared/form-controls/abstract-form-control';
import { BuildingItemDto, BuildingService, SortDto } from '@artemis-software/wr-api';
import { NbPopoverDirective } from '@nebular/theme';
import { firstValueFrom } from 'rxjs';
import { watch } from '@/utils/watch';
import { currentTenantId, isExternalTenant } from '@/utils/admin-utils';
import OrderEnum = SortDto.OrderEnum;

@Component({
  standalone: false,
  selector: 'wr-building-multi-select',
  templateUrl: './building-multi-select.component.html',
  styleUrls: ['./building-multi-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BuildingMultiSelectComponent),
      multi: true,
    },
  ],
})
export class BuildingMultiSelectComponent extends AbstractFormControl<string[]> {

  readonly buildingService = inject(BuildingService);
  readonly popover = viewChild(NbPopoverDirective);

  readonly filterText = signal('');
  readonly page = signal(0);
  readonly buildings = signal<BuildingItemDto[]>([]);
  readonly resetVersion = signal(0);

  readonly tenantId = input<string | undefined>();

  private readonly isExternal = isExternalTenant();
  private readonly ownTenantId = currentTenantId();

  @Output() selectionChange = new EventEmitter<string[]>();

  constructor() {
    super();
    watch([this.filterText], async () => {
      await this.reset();
    });
    watch([this.tenantId], async () => {
      this.value = [];
      this.selectionChange.emit([]);
      await this.reset();
    });
  }

  async reset(): Promise<void> {
    this.resetVersion.update(v => v + 1);
    this.buildings.set([]);
    this.page.set(0);
    await this.loadMore();
  }

  async loadMore(): Promise<void> {
    const versionAtStart = this.resetVersion();
    const page = this.page();
    this.page.update(p => p + 1);
    const more = await firstValueFrom(
      this.buildingService.findAll({
        fullTextSearch: this.filterText() || undefined,
        ...this.tenantScope(this.tenantId()),
        deleted: false,
        page,
        size: 15,
        sort: { sort: 'internalNumber', order: OrderEnum.Asc },
      }),
    );
    if (this.resetVersion() === versionAtStart) {
      this.buildings.update(current => [...current, ...more]);
    }
  }

  get selectedCount(): number {
    return (this.value ?? []).length;
  }

  isSelected(id: string | null | undefined): boolean {
    if (!id) return false;
    return (this.value ?? []).includes(id);
  }

  toggle(id: string | null | undefined): void {
    if (!id) return;
    const current = this.value ?? [];
    this.value = current.includes(id)
      ? current.filter(v => v !== id)
      : [...current, id];
    this.selectionChange.emit(this.value);
  }

  onClear(): void {
    this.value = [];
    this.filterText.set('');
    this.selectionChange.emit([]);
  }

  private tenantScope(tenantId: string | undefined): { ownerTenantId?: string; accessibleForTenantId?: string } {
    if (tenantId && this.isExternal() && tenantId === this.ownTenantId()) {
      return { accessibleForTenantId: tenantId };
    }
    return { ownerTenantId: tenantId };
  }
}
